/**
 * DrManiRevealBuildUp - UX-6, the pre-reveal build-up sequence.
 *
 * After the final answer: a 3 to 4 second staged sequence. Each line fades in
 * over a seed mark growing stage by stage (five stages, matching the five-seed
 * reading), then the quiz moves on. This is anticipation, not a loading
 * screen: it runs even though results are instant.
 *
 * Stage timeline (REVEAL BUILD-UP tab):
 *   line 1 "Reading your answers."            -> stage 1, the seed
 *   line 2 "Checking them against..."         -> stage 2, the seed cracks, a root
 *   line 3 "Your one missing thing, found."   -> stages 3 and 4, a sprout, then leaves
 *   line 4 "And your gardener type is ready." -> stage 5, the small plant, brief pulse
 */
import React, { useEffect, useState } from 'react';
import { DMM_BUILD_UP_LINES } from '../../data/drManiQuiz/uxCopy';
import { DmmSeedStage } from './DrManiIcons';

const LINE_MS = 1000;
/** Extra beat after the last line before advancing. */
const EXIT_BEAT_MS = 350;
const DUAL_STAGE_LINE_INDEX = 2;
const DUAL_STAGE_START = 3;

interface DrManiRevealBuildUpProps {
  onComplete: () => void;
}

const DrManiRevealBuildUp: React.FC<DrManiRevealBuildUpProps> = ({ onComplete }) => {
  const [lineIndex, setLineIndex] = useState(0);
  const [stage, setStage] = useState<number>(DMM_BUILD_UP_LINES[0].stage);

  useEffect(() => {
    const timers: number[] = [];

    DMM_BUILD_UP_LINES.forEach((entry, index) => {
      if (index === 0) return;
      timers.push(
        window.setTimeout(() => {
          setLineIndex(index);
          // Why this matters: line three starts at the sprout before adding leaves.
          setStage(index === DUAL_STAGE_LINE_INDEX ? DUAL_STAGE_START : entry.stage);
        }, index * LINE_MS)
      );
    });

    // Line 3 covers two stages: the sprout appears at its boundary, then leaves
    // appear halfway through. This keeps all five stages visible in order.
    timers.push(
      window.setTimeout(
        () => setStage(DMM_BUILD_UP_LINES[DUAL_STAGE_LINE_INDEX].stage),
        DUAL_STAGE_LINE_INDEX * LINE_MS + LINE_MS / 2
      )
    );

    timers.push(
      window.setTimeout(() => {
        onComplete();
      }, DMM_BUILD_UP_LINES.length * LINE_MS + EXIT_BEAT_MS)
    );

    return () => {
      timers.forEach((t) => window.clearTimeout(t));
    };
  }, [onComplete]);

  const currentLine = DMM_BUILD_UP_LINES[lineIndex].line;
  const isFinalStage = stage === 5;

  return (
    <div className="dmm-buildup-screen">
      <div className="dmm-buildup-card">
        <div
          className={`dmm-buildup-visual ${isFinalStage ? 'dmm-buildup-visual-pulse' : ''}`}
          aria-hidden="true"
        >
          <DmmSeedStage stage={stage} />
        </div>
        <p key={currentLine} className="dmm-buildup-line" role="status" aria-live="polite">
          {currentLine}
        </p>
      </div>
    </div>
  );
};

export default DrManiRevealBuildUp;
